import { useState, useEffect, useCallback } from 'react';

const SESSION_KEY = 'embedded_sso_launch_time';

const MAX_DELAY_MS = 15 * 60 * 1000; // 15-minute safety cap

// Per-encounter countdown anchors. Module-level so each encounter's timer
// survives component unmounts (e.g. switching between encounters and back).
const anchorTimesByEncounter = new Map<string, number>();

/**
 * Delays "Save to EHR" for a configurable period after launch so the EHR
 * data pipeline has time to make the composition resource available. Applies
 * to all EHRs, not just OP.
 *
 * Disabled by default. To enable, set a positive `ehr_save_delay_ms` value in
 * the org's EHR settings. Setting it to 0 or leaving it unset keeps the
 * feature off.
 *
 * @param delayMs - Delay in milliseconds, sourced from ehrSettings.ehr_save_delay_ms.
 *                  Clamped to MAX_DELAY_MS to prevent misconfiguration from
 *                  blocking clinical workflows.
 * @param encounterId - Scopes the countdown to a single encounter; each
 *                      encounter keeps its own independent timer.
 */
export const useEhrSaveDelay = (
  delayMs?: number | null,
  encounterId?: string | null
) => {
  const effectiveDelay = delayMs ? Math.min(delayMs, MAX_DELAY_MS) : 0;

  // Bumped by restart() so the countdown effect re-reads the anchor map.
  const [version, setVersion] = useState(0);

  /** Re-arms this encounter's countdown starting from now (e.g. after a save). */
  const restart = useCallback(() => {
    if (!encounterId) return;
    anchorTimesByEncounter.set(encounterId, Date.now());
    setVersion((v) => v + 1);
  }, [encounterId]);

  const calcRemaining = useCallback(() => {
    if (!encounterId || !effectiveDelay) return 0;
    // Until the encounter's first save, fall back to the SSO launch time.
    const anchorTime =
      anchorTimesByEncounter.get(encounterId) ??
      (Number(sessionStorage.getItem(SESSION_KEY)) || 0);
    if (!anchorTime) return 0;
    return Math.max(0, anchorTime + effectiveDelay - Date.now());
  }, [effectiveDelay, encounterId]);

  const [remainingMs, setRemainingMs] = useState(calcRemaining);

  useEffect(() => {
    setRemainingMs(calcRemaining());

    if (calcRemaining() <= 0) return;

    const interval = setInterval(() => {
      const remaining = calcRemaining();
      setRemainingMs(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
  }, [calcRemaining, version]);

  const isLocked = remainingMs > 0;
  const remainingMinutes = Math.ceil(remainingMs / 60_000);
  const remainingFormatted = formatCountdown(remainingMs);

  return {
    isLocked,
    remainingMs,
    remainingMinutes,
    remainingFormatted,
    restart,
  };
};

/** Formats milliseconds as a m:ss countdown string, e.g. 90_000 -> "1:30". */
const formatCountdown = (ms: number) => {
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};
